import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { TextButton } from 'components/buttons';
import { DateToLocal } from 'components/dates';
import { ShowHide } from 'components/layout';
import { ListingTable } from 'components/listing';
import { Modal, ModalSize, NotificationInline } from 'components/notifications';
import { buildFactValue } from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import { RemovedInfringementIcon } from './RemovedInfringementIcon';
import {
    countPages,
    pageOf,
    pluralise,
    tachoSnapshotItemsPageSize
} from './tachoSnapshotHelpers';

const dateFormat = "DD MMM YYYY";
const clearedItemsTitle = "Cleared infringements";
const defaultArray = [];

const clearedItemHeaders = [
    "Code",
    "Infringement type",
    "Description",
    "Occurred at",
    "GFP cost"
];

// Cleared items are described as additional only when they sit alongside listed ones.
function buildClearedSummary(clearedItemCount, listedItemCount) {
    let clearedNoun = pluralise(clearedItemCount, "infringement was", "infringements were");

    if (listedItemCount > 0) {
        clearedNoun = `additional ${clearedNoun}`;
    }

    return `${clearedItemCount} ${clearedNoun} cleared as part of this review.`;
}

// The notice of how many infringements a review cleared rather than reviewed, with a link to the
// read-only list of them. Shows nothing when none were cleared.
export function ClearedInfringementsNotice(
    {
        clearedItems = defaultArray,
        listedItemCount = 0,
        itemsClearedBefore = null,
        loading = false
    }
) {

    const [clearedPageNumber, setClearedPageNumber] = useState(1);
    const [clearedItemsOpen, setClearedItemsOpen] = useState(false);

    // The cleared items table owns which page is shown, so its choice is mirrored into the state
    // the slice below reads.
    function onClearedPagingChange(newPageNumber) {
        setClearedPageNumber(newPageNumber);
    }

    const activeClearedPage = Math.min(clearedPageNumber, countPages(clearedItems, tachoSnapshotItemsPageSize));

    const clearedRowData = pageOf(clearedItems, activeClearedPage, tachoSnapshotItemsPageSize).map((item) => {
        const reference = item.itemReference;

        return [
            (
                <span key={`tacho-infringement-code-${reference}`} className="inline-items nowrap">
                    <RemovedInfringementIcon item={item} />
                    <span>{buildFactValue(item, "Code")}</span>
                </span>
            ),
            (
                <Fragment key={`tacho-infringement-type-${reference}`}>{buildFactValue(item, "InfringementType")}</Fragment>
            ),
            (
                <Fragment key={`tacho-infringement-description-${reference}`}>{buildFactValue(item, "Description")}</Fragment>
            ),
            (
                <Fragment key={`tacho-infringement-occurred-${reference}`}>{buildFactValue(item, "OccurredAt")}</Fragment>
            ),
            (
                <span key={`tacho-infringement-gfp-cost-${reference}`} className="nowrap">{buildFactValue(item, "GfpCost")}</span>
            )
        ];
    });

    return (
        <Fragment>
            <ShowHide
                evaluator={clearedItems.length > 0}
                show={(
                    <NotificationInline
                        allowClose={false}
                        show
                        intent="warning"
                        text={(
                            <span>
                                {buildClearedSummary(clearedItems.length, listedItemCount)} To review {pluralise(clearedItems.length, "it", "them")} please{" "}
                                <TextButton id="tacho-infringement-view-cleared" onClick={() => setClearedItemsOpen(true)}>click here</TextButton>.
                            </span>
                        )}
                    />
                )}
            />
            <Modal
                title={clearedItemsTitle}
                isOpen={clearedItemsOpen}
                onClose={() => setClearedItemsOpen(false)}
                updateStateOnClose={false}
                size={ModalSize.LARGE}
                fixedHeight
                fixedHeightPercentage={70}
                overflowAuto
            >
                {/* DateToLocal treats null as an invalid date and shows a dash, whereas an absent
                    value is read as now, so a missing date is passed through as null. */}
                <p>
                    Items cleared before <DateToLocal format={dateFormat}>{itemsClearedBefore ?? null}</DateToLocal>
                </p>
                <ListingTable
                    id="tacho-infringement-cleared-items"
                    loadingData={loading}
                    headers={clearedItemHeaders}
                    data={clearedRowData}
                    totalRecordCount={clearedItems.length}
                    showPerPage={false}
                    pageSize={tachoSnapshotItemsPageSize}
                    startPage={activeClearedPage}
                    onPagingChange={onClearedPagingChange}
                    rowClickEnabled={false}
                />
            </Modal>
        </Fragment>
    );
}

ClearedInfringementsNotice.propTypes = {
    clearedItems: PropTypes.array,
    listedItemCount: PropTypes.number,
    itemsClearedBefore: PropTypes.string,
    loading: PropTypes.bool
};
